// src/lib/managers/api/types/apiResponse.ts

import { BannerType } from "../../banner/types/banner";
import type { ViewFilter } from "../../customView/types/customViews";
import { LicensesResponse } from "../../license/types/license.api";
import type { SettingsUpdate } from "../../user/types/settings";
import type { UserConsents, UserProfile } from "../../user/types/user";

export interface MailvistaResponseBody {
    data: any;
}

/**
 * DTO de réponse pour POST v1/me/bootstrap
 * Miroir exact du BootstrapUserResponseDTO backend.
 */
export type BootstrapUserResponseDTO = {
    userProfile: UserProfile;
    isNewUser: boolean;
    tokenRefreshRequired: boolean;
    licenses: LicensesResponse;
};

/**
 * DTO de requête pour PATCH v1/me/userProfile
 * Miroir exact du UpdateUserProfileRequestBody backend (schéma Zod).
 */
export type UpdateUserProfileRequestBody = {
    /** AB#1834 — partiel : fusionné champ à champ côté backend (jamais remplacé en bloc). */
    settings?: SettingsUpdate;
    userConsents?: UserConsents[];
    phoneNumber?: string;
    photoURL?: string;
    isOnboardingDone?: boolean;
    /** AB#1621 — plancher d'analyse rétrospective (ISO string). Persisté set-once côté API. */
    analysisFloorDate?: string;
};

/**
 * DTO de réponse pour PATCH v1/me/userProfile
 */
export type UpdateUserProfileResponseBody = {
    success: boolean;
};

// ─── AI Agents ───────────────────────────────────────────────────────────────

export type WritingAgentResponseDTO = {
    replaceContent: string;
    spentTokens: number;
};

type ConversationAgentToolCallDTO = {
    id: string;
    type: string;
    function: {
        name: string;
        arguments: string;
    };
};

export type ConversationAgentResponseDTO = {
    toolCall: ConversationAgentToolCallDTO;
    spentTokens: number;
};

/**
 * `suite` d'une demande POST v1/ai/conversation-etapes (contrat par étapes, docs/contrat-etapes.md
 * de l'Api-Ai), à partir du deuxième appel d'un tour : un résultat par étape demandée, même `id`.
 */
export type ConversationEtapesSuiteDTO = {
    /** État du tour reçu de l'Api-Ai, renvoyé tel quel, sans le lire ni le modifier. */
    etat: string;
    resultats: Array<{ id: string; resultat: Record<string, unknown> }>;
};

/** Jetons d'un appel de l'Api-Ai (`UsageInfo`) : entrée, sortie, et le modèle appelé. */
type AiUsageDTO = {
    input_tokens: number;
    output_tokens: number;
    model: string;
};

/**
 * Réponse de POST v1/ai/conversation-etapes (`EtapesResponse` de l'Api-Ai, champs absents plutôt
 * que `null`). `type: "etapes"` : exécuter `etapes`, puis rappeler avec `suite`. `type: "fin"` :
 * `message` et sa proposition éventuelle (`action`, `rendezVous` ou `brouillon`), ou un `toolCall`
 * v1. Lue sans confiance par le ConversationManager (`readEtapesResponse`) : une forme inattendue
 * n'y lève jamais.
 */
export type ConversationEtapesResponseDTO = {
    type: "etapes" | "fin";
    etapes?: Array<{
        id: string;
        outil: "lireAgenda" | "lireMails";
        arguments: Record<string, unknown>;
    }>;
    etat?: string;
    /** Fin du tour : la réponse de l'assistant (Markdown simple), ses ref citées entre crochets. */
    message?: string;
    /** Les créneaux cités par la réponse, calculés par l'Api-Ai. */
    creneaux?: Array<{ ref: string; debut: string; fin: string }>;
    /** L'action proposée sur des mails lus, à confirmer. */
    action?: { action: string; refs: string[] };
    /** Le rendez-vous proposé : un appel v1 `createAgendaItem`, à confirmer. */
    rendezVous?: ConversationAgentToolCallDTO;
    /** Le brouillon de mail préparé : un nouveau mail (`objet`), ou une réponse au mail `reponseA`. */
    brouillon?: { texte: string; objet?: string; reponseA?: string };
    toolCall?: ConversationAgentToolCallDTO;
    usage?: AiUsageDTO;
    /** Seulement si l'Api-Backend l'ajoute, comme pour /ai/conversation-agent. */
    spentTokens?: number;
    /** Facultatif (réglage RAISONNEMENT_VISIBLE de l'Api-Ai) : raisonnement de cet appel. */
    raisonnement?: Array<{ type: "pensee" | "etape"; texte: string }>;
    /** Fin du tour : l'échange complet du tour, à garder tel quel et à renvoyer dans `echanges`. */
    echange?: string;
    /** Fin du tour, quand l'app envoie `faits` : les faits retenus, en entier, à la place des précédents. */
    faits?: ConversationFactDTO[];
    /** Fin du tour, si l'assistant a été appelé : place occupée par la conversation (jauge). */
    contexte?: {
        utilise: number;
        max: number;
        /** `utilise` découpé (estimation) par ce qui l'occupe. */
        parts?: {
            consignes: number;
            echanges: number;
            lectures: number;
            reflexion: number;
        };
    };
    /** Fin du tour : le modèle qui a écrit la réponse ; absent quand le code l'a écrite. */
    modeleUtilise?: { nom: string; raisonnement: boolean };
};

/**
 * Mémoire de la conversation (contrat par étapes, docs/refonte-assistant/CONCEPTION-memoire-conversation.md
 * de l'Api-Ai) : les échanges écrits par l'Api-Ai, tels quels, à chaque appel.
 */
export type ConversationEtapesMemoireDTO = {
    echanges: string[];
    /** Les faits retenus dans la conversation ; présents, même vides : l'assistant peut en retenir. */
    faits?: ConversationFactDTO[];
};

/** Un fait retenu dans la conversation : une clé et sa valeur, chacune sur une ligne. */
export type ConversationFactDTO = { cle: string; valeur: string };

/** Réponse de POST v1/ai/conversation-compacter : le résumé, en un échange qui remplace tous les autres. */
export type ConversationCompacterResponseDTO = {
    echange: string;
    /** Le même résumé, en texte, pour l'afficher (l'app ne lit jamais l'échange). */
    resume: string;
    /** Quand l'app envoie `faits` : ceux qui restent après le compactage. */
    faits?: ConversationFactDTO[];
    usage?: AiUsageDTO;
    /** Seulement si l'Api-Backend l'ajoute. */
    spentTokens?: number;
};

/**
 * Historique : un tour à garder, envoyé par l'app après l'avoir affiché. `payload` est opaque pour
 * l'Api-Backend (chiffré tel quel) : l'affichage du tour et ses lectures, ou le résumé d'un compactage.
 */
export type ConversationTurnSaveDTO = {
    conversationId: string;
    /** Le projet où ranger la conversation : lu par l'Api-Backend à sa création seulement. */
    projectId?: string;
    kind: "tour" | "compactage";
    turnId?: string;
    question?: string;
    payload: Record<string, unknown>;
    echange: string;
};

/** Une conversation de la liste de l'historique. */
export type ConversationSummaryDTO = {
    id: string;
    /** Le projet où la conversation est rangée ; absent ou `null` : aucun. */
    projectId?: string | null;
    /** La première question, coupée ; ou le titre donné par l'utilisateur. */
    title: string | null;
    createdAt: string;
    updatedAt: string;
    turnCount: number;
    /** Recherche par mot clé : un court extrait autour du mot trouvé. */
    excerpt?: string;
};

export type ConversationTurnDTO = {
    position: number;
    kind: "tour" | "compactage";
    turnId: string | null;
    question: string | null;
    payload: Record<string, unknown>;
    /**
     * Seulement depuis le dernier compactage (la mémoire de la conversation) ; `null` avant : l'Api-Backend
     * ne le relit pas (audit de performance du 15/10, P5-03).
     */
    echange: string | null;
    /** Le pouce posé sous la réponse du tour ; absent ou `null` : aucun. */
    avis?: ConversationAvis | null;
    createdAt: string;
};

/** Une conversation de l'historique, avec tous ses tours dans l'ordre. */
export type ConversationDetailDTO = {
    id: string;
    /** Le projet où la conversation est rangée ; absent ou `null` : aucun. */
    projectId?: string | null;
    title: string | null;
    createdAt: string;
    updatedAt: string;
    /** Les faits retenus dans la conversation ; absent : un serveur qui ne les connaît pas. */
    faits?: ConversationFactDTO[];
    turns: ConversationTurnDTO[];
};

/**
 * Un projet de l'assistant : un nom, des consignes écrites par l'utilisateur (`null` : aucune) et un
 * périmètre de mails, des adresses de contacts (vide : aucune limite).
 */
export type AssistantProjectDTO = {
    id: string;
    name: string;
    instructions: string | null;
    mailScope: string[];
    createdAt: string;
    updatedAt: string;
    /** Seulement dans la liste : le nombre de conversations rangées dans le projet. */
    conversationCount?: number;
};

/** Somme d'appels de l'assistant, en tokens comptés par le fournisseur. */
export type AssistantUsageTotalsDTO = {
    requests: number;
    inputTokens: number;
    /** Part de `inputTokens` relue depuis le cache du fournisseur. */
    cachedTokens: number;
    outputTokens: number;
    /** Tokens lus et écrits : le nombre à afficher. */
    totalTokens: number;
};

/**
 * Ce que l'assistant a consommé pour l'utilisateur (compteur de l'Api-Backend) : depuis le début du
 * mois civil (`month.start`, UTC), et pour la conversation demandée depuis son premier tour (`null` :
 * aucune demandée). `month.maxTokens` : le 100 % de la jauge du mois, un repère sans blocage.
 */
export type AssistantUsageDTO = {
    month: AssistantUsageTotalsDTO & { start: string; maxTokens?: number };
    conversation: AssistantUsageTotalsDTO | null;
};

/** Ce qu'on donne pour créer un projet, ou (tous les champs facultatifs) pour le modifier. */
export type AssistantProjectInputDTO = {
    name: string;
    instructions?: string | null;
    mailScope?: string[];
};

/**
 * Le projet de la conversation, envoyé à l'Api-Ai avec chaque appel d'un tour (contrat par étapes,
 * bloc `projet`) : ses consignes s'ajoutent à celles de l'assistant pour ce tour ; son périmètre, que
 * l'app applique elle-même aux lectures de mails, lui est seulement signalé.
 */
export type ConversationEtapesProjetDTO = {
    nom: string;
    consignes?: string;
    perimetre?: string[];
};

/** Pouce sous une réponse, tel qu'il est gardé avec le tour dans l'historique. */
export type ConversationAvis = "utile" | "pas_utile";

/** Réponse de POST v1/ai/conversation-avis. */
export type ConversationAvisResponseDTO = {
    /** Vrai si le pouce est posé sur un tour de l'historique ; faux : ce tour n'y est pas. */
    enregistre: boolean;
};

export type AnalysisAgentResponseDTO = {
    analysisResult: string;
    spentTokens: number;
};

/** Entrée rendue par POST v1/mails/searchMailsByCategoriesAndStatus. La date vient de la
 *  table des mails analysés, où elle est obligatoire. */
export type SearchedMailEntryDTO = {
    internetMessageId: string;
    sentDateTime: string;
};

/**
 * DTO de réponse pour GET v1/me/banners/*
 * Miroir sérialisable de BannerData (sans callToActionCallback).
 */
export type BannerResponseDTO = {
    id: string;
    icon: string;
    title: string;
    mainText: string;
    callToActionText?: string;
    callToActionUrl?: string;
    type?: BannerType;
    dismissible?: boolean;
    linkUrl?: string;
    priority?: number;
};

type CustomViewAgentUsageDTO = {
    input_tokens: number;
    output_tokens: number;
    model: string;
};

export type CustomViewAgentResponseDTO = {
    viewFilter: ViewFilter;
    spentTokens: number;
    usage?: CustomViewAgentUsageDTO;
};

// ─── Scoring ─────────────────────────────────────────────────────────────────

type ScoreMailDetailedScores = {
    personnesScore: number;
    titresScore: number;
    teamsScore: number;
    toneScore?: number;
    addresseeScore: number;
};

type ScoreMailRanking = {
    totalScore: number;
    detailedScores: ScoreMailDetailedScores;
    completeDetailedScores: CompleteDetailedScores;
};

export type ScoreMailResponseDTO = {
    ranking: ScoreMailRanking;
    userContextHash: string;
};

// ─── Contacts ────────────────────────────────────────────────────────────────

export type GetContactPhotoResponseDTO = {
    photo: string;
};

/**
 * Un contact retourné par la recherche hybride (GET v1/contacts/search).
 * `score` = combiné (0.6*vectorScore + 0.4*trgmScore). Tous les scores sont dans [0, 1].
 */
export type ContactMatchDTO = {
    contactEmail: string;
    displayName: string;
    occurrences: number; // fréquence d'apparition (signal de pertinence)
    lastSeenAt: string | null; // ISO 8601
    added: boolean;
    score: number; // combiné, 0–1
    vectorScore: number; // sémantique, 0–1
    trgmScore: number; // typo-tolérant, 0–1
};

/**
 * DTO de réponse pour GET v1/contacts/search (contenu de l'enveloppe `{ data }`).
 * `results` est déjà trié par pertinence décroissante côté serveur — ne pas re-trier.
 */
export type SearchContactsResponseDTO = {
    query: string;
    results: ContactMatchDTO[];
    total: number; // = results.length (≤ top_k)
};

/**
 * DTO de réponse pour POST v1/contacts/rag-initialize (AB#1725).
 * `initialized` : l'import du carnet externe a été effectué lors de cet appel.
 * `alreadyInitialized` : le carnet était déjà amorcé (flag de profil) → no-op serveur.
 */
export type InitializeContactRagResponseDTO = {
    initialized: boolean;
    alreadyInitialized: boolean;
};

// ─── User Signatures ─────────────────────────────────────────────────────────

export type UserSignatureDTO = {
    id: string;
    userId: string;
    signatureTitle?: string;
    name?: string;
    jobTitle?: string;
    email?: string;
    companyName?: string;
    phone?: string;
    website?: string;
    other?: string;
    avatar?: string;
    isFavorite: boolean;
};

export type CreateUserSignatureRequestBody = Omit<
    UserSignatureDTO,
    "id" | "userId" | "isFavorite"
>;
export type CreateUserSignatureResponseDTO = UserSignatureDTO;
export type GetUserSignaturesResponseDTO = UserSignatureDTO[];
export type GetUserSignatureByIdResponseDTO = UserSignatureDTO;
export type UpdateUserSignatureRequestBody = UserSignatureDTO;
export type UpdateUserSignatureResponseDTO = UserSignatureDTO;
export type DeleteUserSignatureResponseDTO = { deleted: boolean };

// ─── Todo Items ──────────────────────────────────────────────────────────────

/** Miroir du TodoItemResponseDTO backend. Les todos servis ici sont acceptés par construction. */
export type TodoItemResponseDTO = {
    id: string;
    internetMessageId: string;
    taskSummary: string;
    linkedEventId?: string;
    done: boolean;
    checkedDate: string | null;
};

// ─── CompleteDetailedScores ──────────────────────────────────────────────────────────────

type CompleteDetailedScores = {
    personnes: {
        score: number;
        normalizedScore?: number;
        normalizationThreshold: number;
        categoryWeight: number;
        details: PersonScoreDetails;
    };
    titres: {
        score: number;
        normalizedScore?: number;
        normalizationThreshold: number;
        categoryWeight: number;
        details: TitleScoreDetails;
    };
    teams: {
        score: number;
        normalizedScore?: number;
        normalizationThreshold: number;
        categoryWeight: number;
        details: TeamScoreDetails;
    };
    tone?: {
        score: number;
        normalizedScore?: number;
        categoryWeight: number;
        details: ToneScoreDetails;
    };
    addressee: {
        coefficient: number;
        normalizedScore: number;
        categoryWeight: number;
        details: AddresseeScoreDetails;
    };
    finalCalculation: FinalCalculationDetails;
};

type PersonScoreDetails = {
    contextPersonnes: ScoredItem[];
    participantWeights: {
        sender: number;
        recipient: number;
        cc: number;
    };
    matches: Array<{
        type: string;
        email: string;
        contextScore: number;
        weight: number;
        finalScore: number;
    }>;
    totalMatches: number;
};

type TitleScoreDetails = {
    contextTitres: ScoredItem[];
    textFieldWeights: {
        subject: number;
        aiSummary: number;
        keywords: number;
        message: number;
    };
    matches: Array<{
        section: string;
        match: string;
        contextScore: number;
        weight: number;
        finalScore: number;
    }>;
    sectionsAnalyzed: Array<{
        name: string;
        textLength: number;
        weight: number;
    }>;
};

type TeamScoreDetails = {
    contextTeams: ScoredItem[]; // ← Seul changement vs contextTitres
    textFieldWeights: {
        subject: number;
        aiSummary: number;
        keywords: number;
        message: number;
    };
    matches: Array<{
        section: string;
        match: string;
        contextScore: number;
        weight: number;
        finalScore: number;
    }>;
    sectionsAnalyzed: Array<{
        name: string;
        textLength: number;
        weight: number;
    }>;
};

type AddresseeScoreDetails = {
    currentUserEmail: string;
    userRole: {
        // isSender: boolean;
        isRecipient: boolean;
        isCc: boolean;
        isBcc: boolean;
    };
    addresseeCoefficients: {
        // sender: number;
        recipient: number;
        cc: number;
        bcc: number;
    };
    roleDetected: RoleDetected;
    coefficient: number;
};

enum RoleDetected {
    NONE = "Aucun",
    PRIMARY_RECIPIENT = "Destinataire principal",
    IN_COPY_CC = "En copie (CC)",
    IN_COPY_BCC = "En copie (BCC)",
}

type ToneScoreDetails = {
    toneAnalysis: {
        imperious: number;
        urgency: number;
        directive: number;
        impact: number;
    };
    toneWeights: {
        urgency: number;
        imperious: number;
        directive: number;
        impact: number;
    };
    subScores: {
        urgencyScore: number;
        imperiousScore: number;
        directiveScore: number;
        impactScore: number;
    };
};

type FinalCalculationDetails = {
    rawDimensions: number[];
    normalizedDimensions: number[];
    weights: number[];
    weightedSum: number;
    totalWeight: number;
    averageScore: number;
    calibrationFactor: number;
    finalScore: number;
};

type ScoredItem = {
    score: number;
    items: string[];
};

// ─────────────────────────────────────────────────────────────────────────────

export enum FrontendError {
    // ─── Mail / Scoring ─────────────────────────────────────────────────────
    SCORING_CALCULATION_FAILED = "SCORING_CALCULATION_FAILED",
    NO_USER_CONTEXT_FOUND = "NO_USER_CONTEXT_FOUND",
    USER_CONTEXT_FAILED = "USER_CONTEXT_FAILED",
    GETTING_BASE_MAIL_FAILED = "GETTING_BASE_MAIL_FAILED",
    GETTING_ENRICHED_MAIL_FAILED = "GETTING_ENRICHED_MAIL_FAILED",

    // ─── Organization CRUD ──────────────────────────────────────────────────
    ORGANIZATION_NOT_FOUND = "ORGANIZATION_NOT_FOUND",
    ORGANIZATION_SUSPENDED = "ORGANIZATION_SUSPENDED",
    ORGANIZATION_ALREADY_IN_STATUS = "ORGANIZATION_ALREADY_IN_STATUS",
    ORGANIZATION_CREATE_FAILED = "ORGANIZATION_CREATE_FAILED",
    ORGANIZATION_UPDATE_FAILED = "ORGANIZATION_UPDATE_FAILED",
    ORGANIZATION_DELETE_FAILED = "ORGANIZATION_DELETE_FAILED",

    // ─── Organization Licenses ──────────────────────────────────────────────
    ORGANIZATION_ADD_LICENSES_FAILED = "ORGANIZATION_ADD_LICENSES_FAILED",
    ORGANIZATION_LICENSE_NOT_FOUND = "ORGANIZATION_LICENSE_NOT_FOUND",
    ORGANIZATION_LICENSE_NO_AVAILABLE_SLOT = "ORGANIZATION_LICENSE_NO_AVAILABLE_SLOT",
    ORGANIZATION_LICENSE_USER_ALREADY_ASSIGNED = "ORGANIZATION_LICENSE_USER_ALREADY_ASSIGNED",
    ORGANIZATION_ASSIGN_B2B_USER_FAILED = "ORGANIZATION_ASSIGN_B2B_USER_FAILED",
    ORGANIZATION_LICENSE_EXPIRATION_DATE_FAILED = "ORGANIZATION_LICENSE_EXPIRATION_DATE_FAILED",

    // ─── Organization Users ─────────────────────────────────────────────────
    ORGANIZATION_LIST_USERS_FAILED = "ORGANIZATION_LIST_USERS_FAILED",

    // ─── Bootstrap utilisateur ──────────────────────────────────────────────
    USER_B2C_NOT_ALLOWED = "USER_B2C_NOT_ALLOWED",

    // ─── SDK errors ─────────────────────────────────────────────────────────
    DATAMANAGER_INSTANCIATION_FAILED = "DATAMANAGER_INSTANCIATION_FAILED",
    USER_OR_CONSENT_MANAGER_INITIALISATION_FAILED = "USER_OR_CONSENT_MANAGER_INSTANCIATION_FAILED",
    SCORING_OR_CUSTOMVIEW_MANAGER_INITIALISATION_FAILED = "SCORING_OR_CUSTOMVIEW_MANAGER_INSTANCIATION_FAILED",
    MAILMANAGER_INITIALISATION_FAILED = "MAILMANAGER_INSTANCIATION_FAILED",
    AGENDA_OR_USER_OR_MAIL_STARTUP_FAILED = "AGENDA_OR_USER_OR_MAIL_STARTUP_FAILED",
    MANAGERS_INITIALISATION_FAILED = "MANAGERS_INITIALISATION_FAILED",

    // ─── License / Analysis ─────────────────────────────────────────────────
    NO_VALID_LICENSE_FOR_ANALYSIS = "NO_VALID_LICENSE_FOR_ANALYSIS",
    MONTHLY_ANALYSIS_QUOTA_EXCEEDED = "MONTHLY_ANALYSIS_QUOTA_EXCEEDED",
    NO_VALID_LICENSE_FOR_CUSTOM_VIEW = "NO_VALID_LICENSE_FOR_CUSTOM_VIEW",
    // AB#1621 — mail hors fenêtre rétrospective d'analyse (plancher figé). Ignoré proprement par le SDK.
    OUTSIDE_ANALYSIS_WINDOW = "OUTSIDE_ANALYSIS_WINDOW",
    // AB#1621 — échec de persistance du plancher d'analyse au bootstrap (après retries) → startup-failed.
    ANALYSIS_FLOOR_PERSISTENCE_FAILED = "ANALYSIS_FLOOR_PERSISTENCE_FAILED",

    // ─── Network errors ─────────────────────────────────────────────────────
    NETWORK_UNAVAILABLE_AT_STARTUP = "NETWORK_UNAVAILABLE_AT_STARTUP",
    NETWORK_LOST_DURING_STARTUP = "NETWORK_LOST_DURING_STARTUP",
}
